import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Search, Smartphone } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import type { AdminTraderDossier } from '@pnl/shared';
import { api } from '../lib/api';

function money(cents: number | null, currency: string | null): string {
  if (cents === null) return '—';
  const value = cents / 100;
  return currency === 'USD'
    ? `US$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
    : value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function date(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
}

/**
 * Dossiê do trader: tudo sobre uma pessoa numa tela.
 *
 * O bloco que dá o nome à tela é "O que ele vê no app": a resposta vem da mesma
 * consulta que o app do trader faz, então o time pode afirmar com segurança se
 * uma conta ainda está lá para ser ativada — sem precisar entrar no app do
 * cliente pra conferir.
 */
export function TraderDossierPage() {
  const { userId } = useParams<{ userId: string }>();
  const [term, setTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const byId = useQuery({
    queryKey: ['admin', 'dossier', userId],
    queryFn: () => api.getTraderDossier(userId!),
    enabled: Boolean(userId),
  });
  const bySearch = useQuery({
    queryKey: ['admin', 'dossier-search', searchTerm],
    queryFn: () => api.findTraderDossier(searchTerm),
    enabled: !userId && searchTerm.length > 0,
    retry: false,
  });

  const query = userId ? byId : bySearch;
  const d = query.data as AdminTraderDossier | undefined;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
            Operação
          </p>
          <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
            Dossiê do trader
          </h1>
          <p className="text-ink-500 mt-1 text-sm">
            Tudo de uma pessoa: compras, contas, saques e — o mais importante — exatamente o que
            aparece no aplicativo dela agora.
          </p>
        </div>
        <Link to="/traders">
          <Button size="sm" variant="ghost">
            <ArrowLeft size={14} /> Traders
          </Button>
        </Link>
      </div>

      {!userId && (
        <Card>
          <CardBody>
            <div className="flex items-center gap-2">
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setSearchTerm(term.trim());
                }}
                placeholder="E-mail ou CPF do trader…"
                className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/30 w-80 rounded-lg bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
              />
              <Button size="sm" onClick={() => setSearchTerm(term.trim())}>
                <Search size={14} /> Abrir dossiê
              </Button>
            </div>
            {bySearch.isError && (
              <div className="mt-3">
                <Alert tone="warning">
                  {bySearch.error instanceof PnlApiError
                    ? bySearch.error.api.message
                    : 'Não encontrei esse trader.'}
                </Alert>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {query.isLoading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}

      {d && (
        <>
          {/* Identificação */}
          <Card>
            <CardBody>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-ink-900 text-lg font-semibold">{d.trader.fullName || '—'}</p>
                  <p className="text-ink-500 text-sm">{d.trader.email}</p>
                  <p className="text-ink-500 mt-1 text-xs">
                    CPF {d.trader.cpf ?? d.trader.documentNumber ?? '—'} · {d.trader.phone ?? '—'} ·
                    entrou em {date(d.trader.joinedAt)}
                  </p>
                </div>
                <div className="flex gap-2">
                  {d.trader.marketCode && <Badge tone="brand">{d.trader.marketCode}</Badge>}
                  <Badge tone={d.trader.userStatus === 'ACTIVE' ? 'success' : 'danger'}>
                    {d.trader.userStatus}
                  </Badge>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Divergências */}
          {d.alerts.map((a, i) => (
            <Alert key={i} tone={a.tone} title={a.title}>
              {a.detail}
            </Alert>
          ))}

          {/* O QUE ELE VÊ NO APP — o bloco que responde a pergunta do dia a dia */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle>
                  <span className="inline-flex items-center gap-2">
                    <Smartphone size={16} /> O que aparece no app dele agora
                  </span>
                </CardTitle>
                <CardDescription>
                  Consultado com a mesma regra que o aplicativo usa — se não está listado aqui, o
                  trader não vê.
                </CardDescription>
              </div>
            </CardHeader>
            <CardBody>
              <p className="text-ink-700 mb-2 text-sm font-semibold">
                Contas a ativar ({d.purchases.filter((p) => p.availableInApp).length})
              </p>
              {d.purchases.filter((p) => p.availableInApp).length === 0 ? (
                <p className="text-ink-500 text-sm">Nenhuma compra disponível para ativar.</p>
              ) : (
                <ul className="text-ink-700 space-y-1 text-sm">
                  {d.purchases
                    .filter((p) => p.availableInApp)
                    .map((p) => (
                      <li key={p.subscriptionId}>
                        • {p.planName} ({p.marketCode}) — paga em {date(p.paidAt)}
                        {p.contractStatus !== 'SIGNED' && (
                          <span className="text-warning-600"> · contrato não assinado</span>
                        )}
                      </li>
                    ))}
                </ul>
              )}

              <p className="text-ink-700 mb-2 mt-5 text-sm font-semibold">
                Contas já criadas ({d.accounts.length})
              </p>
              {d.accounts.length === 0 ? (
                <p className="text-ink-500 text-sm">Nenhuma conta virtual.</p>
              ) : (
                <ul className="text-ink-700 space-y-1 text-sm">
                  {d.accounts.map((a) => (
                    <li key={a.id}>
                      • {a.label} — {a.phase} / {a.status}
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {/* Compras */}
          <Card>
            <CardHeader>
              <CardTitle>Compras ({d.purchases.length})</CardTitle>
            </CardHeader>
            <CardBody>
              {d.purchases.length === 0 ? (
                <EmptyState title="Nenhuma compra" description="Este usuário nunca comprou." />
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>Plano</TH>
                      <TH>Assinatura</TH>
                      <TH>Comprada</TH>
                      <TH className="text-right">Valor</TH>
                      <TH>Contrato</TH>
                      <TH>Situação</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {d.purchases.map((p) => (
                      <TR key={p.subscriptionId}>
                        <TD>
                          <div className="text-ink-900 text-sm font-semibold">{p.planName}</div>
                          <div className="text-ink-500 text-xs">
                            {p.planCode} ({p.marketCode})
                          </div>
                        </TD>
                        <TD>
                          <Badge tone={p.status === 'ACTIVE' ? 'success' : 'neutral'}>
                            {p.status}
                          </Badge>
                        </TD>
                        <TD className="text-ink-600 text-xs tabular-nums">{date(p.createdAt)}</TD>
                        <TD className="text-right text-xs tabular-nums">
                          {money(p.amountCents, p.currency)}
                          {p.paidAt && <div className="text-ink-500">pago {date(p.paidAt)}</div>}
                        </TD>
                        <TD className="text-xs">{p.contractStatus ?? '—'}</TD>
                        <TD className="text-xs">
                          {p.refundedAt ? (
                            <span className="text-danger-600 font-semibold">
                              estornada {date(p.refundedAt)}
                            </span>
                          ) : p.hasAccount ? (
                            'conta criada'
                          ) : p.availableInApp ? (
                            <span className="text-brand-600 font-semibold">disponível no app</span>
                          ) : (
                            '—'
                          )}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </CardBody>
          </Card>

          {/* Contas + teto dos primeiros saques */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Contas ({d.accounts.length})</CardTitle>
                <CardDescription>
                  A coluna de saques mostra quantos dos {d.accounts[0]?.firstWithdrawalsLimit ?? 7}{' '}
                  primeiros (com teto por solicitação) já foram concedidos.
                </CardDescription>
              </div>
            </CardHeader>
            <CardBody>
              {d.accounts.length === 0 ? (
                <EmptyState title="Nenhuma conta" description="Nada foi ativado ainda." />
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>Conta</TH>
                      <TH>Fase</TH>
                      <TH className="text-right">Saldo</TH>
                      <TH className="text-right">Colchão</TH>
                      <TH>Primeiros saques</TH>
                      <TH>Aberta</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {d.accounts.map((a) => {
                      const currency = a.marketCode === 'FOREX' ? 'USD' : 'BRL';
                      return (
                        <TR key={a.id}>
                          <TD>
                            <div className="text-ink-900 text-sm font-semibold">{a.label}</div>
                            <div className="text-ink-500 font-mono text-xs">
                              {a.subAccountNelogicaId}
                            </div>
                          </TD>
                          <TD>
                            <Badge tone={a.status === 'BLOWN' ? 'danger' : 'success'}>
                              {a.phase}
                            </Badge>
                          </TD>
                          <TD className="text-right text-xs tabular-nums">
                            {money(a.currentBalanceCents, currency)}
                          </TD>
                          <TD className="text-right text-xs tabular-nums">
                            {money(a.cushionCents, currency)}
                          </TD>
                          <TD className="text-xs">
                            {a.firstWithdrawalsExhausted ? (
                              <span className="text-ink-700">
                                <span className="font-semibold">
                                  {a.withdrawalsGranted}/{a.firstWithdrawalsLimit}
                                </span>{' '}
                                — teto cumprido, saques livres
                              </span>
                            ) : (
                              <span className="text-ink-700">
                                <span className="font-semibold">
                                  {a.withdrawalsGranted}/{a.firstWithdrawalsLimit}
                                </span>{' '}
                                — teto de {money(a.firstWithdrawalCapCents, currency)} por saque
                              </span>
                            )}
                          </TD>
                          <TD className="text-ink-600 text-xs tabular-nums">{date(a.openedAt)}</TD>
                        </TR>
                      );
                    })}
                  </TBody>
                </Table>
              )}
            </CardBody>
          </Card>

          {/* Saques */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Saques ({d.withdrawals.requestedCount})</CardTitle>
                <CardDescription>
                  {d.withdrawals.grantedCount} concedido(s), {d.withdrawals.paidCount} pago(s) ·
                  total repassado {money(d.withdrawals.totalPaidCents, 'BRL')}
                </CardDescription>
              </div>
            </CardHeader>
            <CardBody>
              {d.withdrawals.items.length === 0 ? (
                <EmptyState title="Nenhum saque" description="Nunca solicitou." />
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>Conta</TH>
                      <TH className="text-right">Valor</TH>
                      <TH>Status</TH>
                      <TH>Solicitado</TH>
                      <TH>Pago</TH>
                      <TH>NF</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {d.withdrawals.items.map((w) => (
                      <TR key={w.id}>
                        <TD className="text-xs">{w.accountLabel}</TD>
                        <TD className="text-right text-xs tabular-nums">
                          {money(w.amountCents, 'BRL')}
                        </TD>
                        <TD>
                          <Badge
                            tone={
                              w.status === 'PAID'
                                ? 'success'
                                : w.status === 'REJECTED'
                                  ? 'danger'
                                  : 'warning'
                            }
                          >
                            {w.status}
                          </Badge>
                          {w.rejectionReason && (
                            <div className="text-danger-600 mt-1 text-xs">{w.rejectionReason}</div>
                          )}
                        </TD>
                        <TD className="text-ink-600 text-xs tabular-nums">{date(w.requestedAt)}</TD>
                        <TD className="text-ink-600 text-xs tabular-nums">{date(w.paidAt)}</TD>
                        <TD className="text-xs">{w.hasInvoiceFile ? 'anexada' : '—'}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
